
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

const testimonials = [
  {
    quote: "Snabba upphandlingar revolutionerade vår anbudsprocess. Vi kunde lämna in ett konkurrenskraftigt anbud på bara en vecka istället för en månad.",
    author: "Sofia Björk",
    position: "Upphandlingschef",
    avatar: "https://randomuser.me/api/portraits/women/32.jpg",
    rating: 5
  },
  {
    quote: "Systemet identifierade flera kritiska faktorer i upphandlingen som vi hade missat. Det gav oss en klar konkurrensfördel i anbudsprocessen.",
    author: "Markus Lind",
    position: "VD",
    avatar: "https://randomuser.me/api/portraits/men/54.jpg",
    rating: 5
  },
  {
    quote: "Precisionen i analyserna och de skräddarsydda rekommendationerna sparade oss oräkneliga timmar av arbete. Vi kommer definitivt att använda tjänsten igen.",
    author: "Emma Persson",
    position: "Anbudsstrateg",
    avatar: "https://randomuser.me/api/portraits/women/44.jpg",
    rating: 4
  }
];

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  const nextTestimonial = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };
  
  const prevTestimonial = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };
  
  useEffect(() => {
    const interval = setInterval(() => {
      nextTestimonial();
    }, 8000);
    
    return () => clearInterval(interval);
  }, [activeIndex, isAnimating]);
  
  return (
    <section id="testimonials" className="py-20 bg-white puzzle-pattern">
      <div className="container mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="inline-block rounded-full bg-yellow-300/20 px-4 py-1.5 mb-4">
            <span className="text-sm font-montserrat font-bold text-darkgray">Omdömen</span>
          </div>
          <h2 className="mb-6 text-2xl md:text-3xl font-montserrat font-bold text-darkgray">Vad våra kunder säger</h2>
          <p className="text-lg font-opensans">
            Upptäck hur Snabba upphandlingar har hjälpt företag att optimera sina anbud och vinna fler upphandlingar.
          </p>
        </div>
        
        <div className="relative max-w-4xl mx-auto">
          {/* Navigation arrows */}
          <button 
            onClick={prevTestimonial}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-10 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center text-darkgray hover:bg-yellow-500 hover:text-white transition-colors duration-300"
            aria-label="Previous testimonial"
          >
            <ChevronLeft size={20} />
          </button>
          
          <button 
            onClick={nextTestimonial}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 md:translate-x-12 z-10 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center text-darkgray hover:bg-yellow-500 hover:text-white transition-colors duration-300"
            aria-label="Next testimonial"
          >
            <ChevronRight size={20} />
          </button>
          
          {/* Testimonial card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/5 rounded-full transform translate-x-16 -translate-y-16"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-yellow-300/10 rounded-full transform -translate-x-16 translate-y-16"></div>
            
            <div 
              className={`transition-opacity duration-500 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}
            >
              {/* Quote mark */}
              <div className="mb-6 text-yellow-300">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.4 24H8V32H16V40H8C5.87827 40 3.84344 39.1571 2.34315 37.6569C0.842855 36.1566 0 34.1217 0 32V24C0 21.8783 0.842855 19.8434 2.34315 18.3431C3.84344 16.8429 5.87827 16 8 16H14.4V24ZM38.4 24H32V32H40V40H32C29.8783 40 27.8434 39.1571 26.3431 37.6569C24.8429 36.1566 24 34.1217 24 32V24C24 21.8783 24.8429 19.8434 26.3431 18.3431C27.8434 16.8429 29.8783 16 32 16H38.4V24Z" fill="currentColor"/>
                </svg>
              </div>
              
              {/* Quote text */}
              <p className="text-xl md:text-2xl mb-8 text-darkgray font-opensans">
                {testimonials[activeIndex].quote}
              </p>
              
              {/* Author info */}
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center">
                  <Avatar className="w-12 h-12 border-2 border-yellow-300">
                    <AvatarImage 
                      src={testimonials[activeIndex].avatar} 
                      alt={testimonials[activeIndex].author}
                    />
                    <AvatarFallback>{testimonials[activeIndex].author.substring(0, 2)}</AvatarFallback>
                  </Avatar>
                  <div className="ml-4">
                    <h4 className="font-montserrat font-bold text-darkgray">{testimonials[activeIndex].author}</h4>
                    <p className="text-sm text-darkgray/70 font-opensans">{testimonials[activeIndex].position}</p>
                  </div>
                </div>
                
                {/* Rating */}
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i}
                      size={18}
                      className={i < testimonials[activeIndex].rating ? "text-yellow-500 fill-yellow-500" : "text-gray-300"}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Indicator dots */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (isAnimating) return;
                  setIsAnimating(true);
                  setActiveIndex(index);
                  setTimeout(() => setIsAnimating(false), 500);
                }}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === activeIndex ? 'bg-yellow-500 w-6' : 'bg-yellow-300/50'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
